import { useRef, useCallback, type RefObject } from 'react';

/**
 * Hook that adds swipe-down-to-dismiss behavior to a mobile bottom sheet.
 * Put touch handlers on the drag handle; pass sheetRef (or your own ref) to the sheet container.
 */
export function useSwipeDismiss(onClose: () => void, externalRef?: RefObject<HTMLDivElement | null>, threshold = 80) {
  const internalRef = useRef<HTMLDivElement>(null);
  const sheetRef = externalRef || internalRef;
  const startY = useRef(0);
  const currentY = useRef(0);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
    currentY.current = startY.current;
    if (sheetRef.current) {
      sheetRef.current.style.transition = 'none';
    }
  }, [sheetRef]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    currentY.current = e.touches[0].clientY;
    const dy = currentY.current - startY.current;
    if (dy > 0 && sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${dy}px)`;
    }
  }, [sheetRef]);

  const onTouchEnd = useCallback(() => {
    const dy = currentY.current - startY.current;
    if (sheetRef.current) {
      sheetRef.current.style.transition = 'transform 0.2s ease-out';
      if (dy > threshold) {
        sheetRef.current.style.transform = `translateY(100%)`;
        setTimeout(onClose, 200);
      } else {
        sheetRef.current.style.transform = 'translateY(0)';
      }
    }
  }, [onClose, threshold, sheetRef]);

  return { sheetRef, onTouchStart, onTouchMove, onTouchEnd };
}
